import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  type FreshStrawStageGradeEntry,
  useUpdateFreshStrawDegeneratesMutation,
  useVerifyFreshStrawBillableMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useFreshStrawFertilizations } from '../../hooks/useFreshStrawQueries';
import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';
import {
  useFreshStrawDetailReport,
  useFreshStrawSummaryReport,
} from '../../reports';
import { selectFertilizationRowMath } from '../../selectors';
import type { FreshStrawRow } from '../FreshStrawActionPanel/payload';
import { FreshStrawFertEditDialog } from '../FreshStrawFertEditDialog';
import { FreshStrawStageGradeDialog } from '../FreshStrawStageGradeDialog';
import { DesktopGrid } from './DesktopGrid';
import { MobileCardList } from './MobileCardList';
import {
  computeDegenerates,
  FertilizationLike,
  getOrInitRow,
  toggleSelection,
  upsertRowEntries,
} from './shared';

/**
 * Responsive fresh-straw fertilization surface.
 * - Desktop (≥1024px): ag-grid with checkbox selection + inline row math
 * - Mobile/tablet (<1024px): card list with large touch targets
 *
 * Both surfaces share selection state, the stage/grade dialog, and the
 * row-math validation effect. The parent page owns `selectedRows` so the
 * action panel can react to it.
 */

const DESKTOP_BREAKPOINT = '(min-width: 1024px)';

export interface FreshStrawEmbryoPanelProps {
  session: FreshStrawSession;
  selectedRows: FreshStrawRow[];
  onSelectionChange: (rows: FreshStrawRow[]) => void;
  rowMathOk: boolean;
  onRowMathChange: (ok: boolean) => void;
  className?: string;
}

export function FreshStrawEmbryoPanel({
  session,
  selectedRows,
  onSelectionChange,
  rowMathOk,
  onRowMathChange,
  className,
}: FreshStrawEmbryoPanelProps) {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT);

  const { data, isFetching, error, refetch } = useFreshStrawFertilizations({
    fertDate: session.fertDate || undefined,
    fertSite: session.fertSite || undefined,
    accountId: session.accountId || undefined,
    freshShipmentId: session.freshShipmentId || undefined,
  });

  const [updateDegenerates, { isLoading: isUpdatingDegenerates }] =
    useUpdateFreshStrawDegeneratesMutation();
  const [verifyBillable, { isLoading: isVerifyingBillable }] =
    useVerifyFreshStrawBillableMutation();
  const [generateSummaryReport, { isPending: isExporting }] =
    useFreshStrawSummaryReport();
  const [generateDetailReport, { isPending: isExportingDetail }] =
    useFreshStrawDetailReport();
  const isBulkBusy =
    isUpdatingDegenerates ||
    isVerifyingBillable ||
    isExporting ||
    isExportingDetail;

  const fertilizations = useMemo<FertilizationLike[]>(
    () => (data ?? []) as FertilizationLike[],
    [data],
  );

  // Propagate the row-math invariant to the parent whenever the loaded
  // fertilizations change. Running in useEffect (not during render) so we
  // don't hit React's setState-in-render warning.
  useEffect(() => {
    const currentOk = selectFertilizationRowMath(
      fertilizations.map((f) => ({
        clientTransfer: f.clientTransfer,
        clientFrozen: f.clientFrozen,
        clientDiscarded: f.clientDiscarded,
        clientShipped: f.clientShipped,
      })),
    );
    if (currentOk !== rowMathOk) {
      onRowMathChange(currentOk);
    }
  }, [fertilizations, rowMathOk, onRowMathChange]);

  const [stageGradeDialogRowId, setStageGradeDialogRowId] = useState<
    string | null
  >(null);
  const [fertEditDialogRowId, setFertEditDialogRowId] = useState<string | null>(
    null,
  );

  const handleToggleRow = (fertilizationId: string) => {
    onSelectionChange(toggleSelection(selectedRows, fertilizationId));
  };

  const handleSetRowEntries = (
    fertilizationId: string,
    entries: FreshStrawStageGradeEntry[],
  ) => {
    onSelectionChange(upsertRowEntries(selectedRows, fertilizationId, entries));
  };

  const dialogRow = stageGradeDialogRowId
    ? getOrInitRow(selectedRows, stageGradeDialogRowId)
    : null;

  const summaryLabel = `${fertilizations.length} loaded · ${selectedRows.length} selected`;

  const hasRows = fertilizations.length > 0;

  const handleAddDiscards = useCallback(async () => {
    const request = fertilizations
      .map(computeDegenerates)
      .filter(
        (r): r is { fertilizationId: string; degenerates: number } =>
          r !== null,
      );

    if (request.length === 0) {
      dispatch(
        addToast({
          severity: 'info',
          summary: 'Nothing to update',
          detail:
            'No fertilizations have shipped counts to derive degenerates from.',
          life: ToastLife.SHORT,
        }),
      );
      return;
    }

    try {
      await updateDegenerates(request).unwrap();
      await refetch();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Discards updated',
          detail: `Degenerates recomputed for ${request.length} fertilization${request.length === 1 ? '' : 's'}.`,
          life: ToastLife.SHORT,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Failed to update discards',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  }, [fertilizations, updateDegenerates, refetch, dispatch]);

  const handleVerifyBillable = useCallback(async () => {
    const ids = fertilizations
      .map((f) => f.fertilizationId)
      .filter((id): id is string => Boolean(id));

    if (ids.length === 0) return;

    try {
      const response = await verifyBillable(ids).unwrap();
      await refetch();
      const alreadyCount = response.results.filter(
        (r) => r === 'Already Verified',
      ).length;
      const newCount = response.results.filter((r) => r === 'Success').length;
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Billable shipped verified',
          detail:
            alreadyCount > 0
              ? `${newCount} newly verified, ${alreadyCount} already verified.`
              : `Marked ${newCount} fertilization${newCount === 1 ? '' : 's'} verified.`,
          life: ToastLife.SHORT,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Failed to verify billable shipped',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  }, [fertilizations, verifyBillable, refetch, dispatch]);

  const bulkDisabled = !hasRows || isBulkBusy;

  const handleExportSummary = useCallback(async () => {
    if (!session.fertDate) return;
    await generateSummaryReport({
      fertilizationDate: session.fertDate,
      fertilizationSite: session.fertSite || undefined,
      accountId: session.accountId || undefined,
      freshShipmentId: session.freshShipmentId || undefined,
    });
  }, [
    generateSummaryReport,
    session.fertDate,
    session.fertSite,
    session.accountId,
    session.freshShipmentId,
  ]);

  const handleExportDetail = useCallback(async () => {
    if (!session.fertDate) return;
    await generateDetailReport({
      fertilizationDate: session.fertDate,
      fertilizationSite: session.fertSite || undefined,
      accountId: session.accountId || undefined,
      freshShipmentId: session.freshShipmentId || undefined,
    });
  }, [
    generateDetailReport,
    session.fertDate,
    session.fertSite,
    session.accountId,
    session.freshShipmentId,
  ]);

  return (
    <section
      className={cn(
        'ring-border bg-background flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg ring-1',
        className,
      )}
      data-slot="fresh-straw-embryo-panel"
    >
      <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-2 border-b px-3 py-1.5">
        <div className="flex items-baseline gap-3">
          <h2 className="text-xs font-semibold tracking-wide uppercase">
            Fertilizations
          </h2>
          <span className="text-muted-foreground text-xs">{summaryLabel}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={handleAddDiscards}
            disabled={bulkDisabled}
            data-testid="bulk-add-discards"
          >
            Add discards
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={handleVerifyBillable}
            disabled={bulkDisabled || !rowMathOk}
            data-testid="bulk-verify-billable"
            title={
              !rowMathOk
                ? 'Row math must pass before verifying billable shipped'
                : undefined
            }
          >
            Verify billable shipped
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={handleExportSummary}
            disabled={!session.fertDate || isExporting}
            data-testid="bulk-export-summary"
          >
            Export summary
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={handleExportDetail}
            disabled={!session.fertDate || isExportingDetail}
            data-testid="bulk-export-detail"
          >
            Export detail
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {isDesktop ? (
          <DesktopGrid
            rows={fertilizations}
            selectedRows={selectedRows}
            loading={isFetching}
            onToggleRow={handleToggleRow}
            onOpenStageGrade={setStageGradeDialogRowId}
            onOpenFertEdit={setFertEditDialogRowId}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <MobileCardList
              rows={fertilizations}
              selectedRows={selectedRows}
              loading={isFetching}
              error={error}
              onToggleRow={handleToggleRow}
              onOpenStageGrade={setStageGradeDialogRowId}
              onOpenFertEdit={setFertEditDialogRowId}
            />
          </div>
        )}
      </div>

      <FreshStrawStageGradeDialog
        open={stageGradeDialogRowId !== null}
        onOpenChange={(open) => {
          if (!open) setStageGradeDialogRowId(null);
        }}
        rowLabel={
          fertilizations.find(
            (f) => f.fertilizationId === stageGradeDialogRowId,
          )?.mating ??
          stageGradeDialogRowId ??
          undefined
        }
        initialEntries={dialogRow?.entries ?? null}
        onSave={(entries) => {
          if (stageGradeDialogRowId) {
            handleSetRowEntries(stageGradeDialogRowId, entries);
          }
        }}
      />

      {(() => {
        const fertRow = fertEditDialogRowId
          ? fertilizations.find(
              (f) => f.fertilizationId === fertEditDialogRowId,
            )
          : null;
        return (
          <FreshStrawFertEditDialog
            open={fertEditDialogRowId !== null}
            onOpenChange={(open) => {
              if (!open) setFertEditDialogRowId(null);
            }}
            fertilizationId={fertEditDialogRowId}
            mating={fertRow?.mating}
            initial={
              fertRow
                ? {
                    ivcNumber: fertRow.ivcNumber,
                    cleaved: fertRow.cleaved,
                    transferClient: fertRow.clientTransfer,
                    frozeClient: fertRow.clientFrozen,
                    discardClient: fertRow.clientDiscarded,
                    heldTransfer: fertRow.heldTransfer,
                    heldFrz: fertRow.heldFrozen,
                    discardedGrade1: fertRow.gradeOneDiscarded,
                    discardedGrade2: fertRow.gradeTwoDiscarded,
                    discardedGrade3: fertRow.gradeThreeDiscarded,
                    degenerates: fertRow.degenerates,
                    loadedInStraws: fertRow.inStraws,
                  }
                : undefined
            }
            onSaved={refetch}
          />
        );
      })()}
    </section>
  );
}
